import { useTranslation } from 'react-i18next'

import { API_PARAM_IS_REDESIGN_CONTEXT } from '../../../../../../constants/api'
import { NOTIFICATION_TYPE } from '../../../../../../constants/common'
import { parseServerValidation } from '../../../../../../utils/validation'
import { useAuthCaptchaWindowWatcher } from '../../../../Auth.hooks'
import { login, sendCodePhone, verifyCodePhone } from '../../../Login.api'
import {
  LOGIN_FORM_PHONE_CAPTCHA_TOKEN,
  LOGIN_FORM_PHONE_NOTIFICATION_GET_CAPTCHA_ERROR,
} from '../../LoginFormPhone.constants'
import {
  parseLoginFormPhoneNumberServerError,
  parseLoginFormPhoneVerificationServerError,
} from '../../LoginFormPhone.utils'

export function useLoginFormPhoneSubmitHandlers({
  recaptchaRef,
  setLoading,
  setSubmittedPhoneData,
  setUserStatus,
  submittedPhoneData,
  onRemoveNotification,
  onSetNotification,
  onSuccessLogin,
}) {
  const { t } = useTranslation()

  function handleCloseCaptchaWindow() {
    setLoading(false)
  }

  useAuthCaptchaWindowWatcher({ onClose: handleCloseCaptchaWindow })

  async function getCaptchaToken() {
    recaptchaRef.current?.reset()

    let token

    try {
      token = await recaptchaRef.current.executeAsync()

      onRemoveNotification(LOGIN_FORM_PHONE_NOTIFICATION_GET_CAPTCHA_ERROR)
    } catch (error) {
      console.error('Error reCAPTCHA:', error)

      onSetNotification({
        id: LOGIN_FORM_PHONE_NOTIFICATION_GET_CAPTCHA_ERROR,
        text: t('Auth.NotificationLoginFormGetCaptchaErrorKey'),
        type: NOTIFICATION_TYPE.error,
      })

      setLoading(false)

      return
    }

    return token
  }

  async function handleSubmitPhoneNumber(data, { setErrors }) {
    setLoading(true)

    const token = await getCaptchaToken()

    if (token) {
      sendCodePhone({
        ...data,
        [API_PARAM_IS_REDESIGN_CONTEXT]: true,
        [LOGIN_FORM_PHONE_CAPTCHA_TOKEN]: token,
      })
        .then(response => {
          setUserStatus(response.data.status)
          setSubmittedPhoneData(data)
        })
        .catch(error => {
          setErrors(
            parseServerValidation(parseLoginFormPhoneNumberServerError(error))
          )
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }

  async function handleResendCode({ setErrors }) {
    setLoading(true)

    const token = await getCaptchaToken()

    if (token) {
      return sendCodePhone({
        ...submittedPhoneData,
        [LOGIN_FORM_PHONE_CAPTCHA_TOKEN]: token,
      })
        .catch(error => {
          setErrors(
            parseServerValidation(parseLoginFormPhoneNumberServerError(error))
          )
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }

  function handleSubmitPhoneVerification(data, { setErrors }) {
    setLoading(true)

    verifyCodePhone({ ...data, ...submittedPhoneData })
      .then(response => {
        if (response.data.one_time_password) {
          return login({
            password: response.data.one_time_password,
            username: submittedPhoneData.number,
          })
        }
      })
      .then(onSuccessLogin)
      .catch(error => {
        setErrors(
          parseServerValidation(
            parseLoginFormPhoneVerificationServerError(error)
          )
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return {
    handleResendCode,
    handleSubmitPhoneNumber,
    handleSubmitPhoneVerification,
  }
}
